'use client';

import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { TargetMarketFitTable } from './TargetMarketFitTable';
import {
  Building2, BarChart3, Globe, Users, CreditCard, Shield, ArrowRight,
  CalendarCheck, Utensils, ClipboardList, Bell, Lock, Plug, Wrench,
  LayoutDashboard, Layers, Truck, FileText, Star, Zap, Banknote,
  Wallet, Smartphone, Network, ChefHat, ShoppingCart, Coffee, Receipt,
  ShieldCheck, ScanLine, ScrollText, Fingerprint,
  Brain, Bot, Cpu, Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { FEATURES_CONTENT } from '@/core/i18n/content/features.content';
import type { FeaturesContent } from '@/core/i18n/content/features.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Icon maps — icons by [categoryIdx][featureIdx]                             */
/* -------------------------------------------------------------------------- */

const CATEGORY_ICONS: LucideIcon[][] = [
  [Building2, CalendarCheck, LayoutDashboard, Utensils],
  [BarChart3, Globe, FileText, ClipboardList],
  [Users, Star, Bell, CreditCard],
  [Layers, Lock, Plug, Wrench],
  [Banknote, Wallet, Smartphone, Network],
  [ChefHat, ShoppingCart, Coffee, Receipt],
  [ShieldCheck, ScanLine, ScrollText, Fingerprint],
  [Brain, Bot, Cpu, Sparkles],
];

/* -------------------------------------------------------------------------- */
/*  FeatureCard                                                                */
/* -------------------------------------------------------------------------- */

function FeatureCard({ title, description, icon: Icon, index }: { title: string; description: string; icon: LucideIcon; index: number }) {
  const { t } = useTranslation();
  return (
    <div
      id={`features-card-${index}`}
      className={cn('group relative rounded-xl border border-hms-border bg-hms-card p-5', 'transition-all duration-300', 'hover:border-hms-gold/20 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20')}
    >
      <div className={cn('mb-3.5 flex h-10 w-10 items-center justify-center rounded-lg', 'bg-hms-gold/10 transition-colors duration-300 group-hover:bg-hms-gold/15')}>
        <Icon className="h-5 w-5 text-hms-gold" />
      </div>
      <h4 className="mb-1.5 text-sm font-bold text-hms-text tracking-tight">{title}</h4>
      <p className="text-[13px] leading-relaxed text-hms-textSub">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CategorySection                                                            */
/* -------------------------------------------------------------------------- */

function CategorySection({ category, categoryIdx, globalOffset }: {
  category: FeaturesContent['categories'][number];
  categoryIdx: number;
  globalOffset: number;
}) {
  const icons = CATEGORY_ICONS[categoryIdx] ?? [];

  return (
    <section
      id={`features-category-${categoryIdx}`}
      className={cn('py-16 sm:py-20', categoryIdx % 2 === 0 ? 'bg-hms-bg' : 'bg-hms-surface')}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <h3
            id={`features-category-title-${categoryIdx}`}
            className={cn('text-2xl font-extrabold tracking-tight sm:text-3xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}
          >
            {category.title}
          </h3>
          <p className="mt-2 text-sm text-hms-textSub sm:text-base max-w-2xl">{category.description}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {category.features.map((feat, idx) => (
            <FeatureCard
              key={idx}
              title={feat.title}
              description={feat.description}
              icon={icons[idx] ?? Building2}
              index={globalOffset + idx}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  FeaturesPage — Main Export                                                 */
/* -------------------------------------------------------------------------- */

export function FeaturesPage() {
  const c = usePageContent(FEATURES_CONTENT);

  return (
    <main id="features-page">
      {/* Hero */}
      <section id="features-hero" className="relative w-full overflow-hidden bg-hms-bg">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.07) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-4xl px-4 pb-14 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-36">
          <h1
            id="features-hero-title"
            className={cn('font-poppins text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}
          >
            {c.hero.title}
          </h1>
          <p id="features-hero-subtitle" className="mx-auto mt-5 max-w-2xl text-base text-hms-textSub sm:text-lg">
            {c.hero.subtitle}
          </p>
          <div id="features-stats" className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4 max-w-2xl mx-auto">
            {c.stats.map((stat, idx) => (
              <div key={idx} id={`features-stat-${idx}`} className="flex flex-col items-center rounded-xl border border-hms-border bg-hms-card px-4 py-4">
                <span className="text-xl font-extrabold text-hms-gold tracking-tight">{stat.value}</span>
                <span className="mt-0.5 text-[11px] font-medium text-hms-textSub">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Categories */}
      {c.categories.map((category, idx) => {
        const offset = c.categories.slice(0, idx).reduce((sum, cat) => sum + cat.features.length, 0);
        return <CategorySection key={idx} category={category} categoryIdx={idx} globalOffset={offset} />;
      })}

      {/* Target Market Fit */}
      <TargetMarketFitTable />

      {/* Bottom CTA */}
      <section id="features-cta" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-24">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <Zap className="mx-auto h-8 w-8 text-hms-gold mb-4" />
          <h2 id="features-cta-title" className={cn('text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>
            {c.cta.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-hms-textSub sm:text-base">{c.cta.subtitle}</p>
          <div id="features-cta-actions" className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <LocaleLink
              id="features-cta-trial-btn"
              href="/register-hotel"
              className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}
            >
              {c.cta.trialBtn}
              <ArrowRight className="h-4 w-4" />
            </LocaleLink>
            <LocaleLink
              id="features-cta-demo-btn"
              href="/contact"
              className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}
            >
              {c.cta.demoBtn}
            </LocaleLink>
          </div>
        </div>
      </section>
    </main>
  );
}
